import React, { useEffect, useState } from "react";
import axios from "../context/axios";
import toast, { Toaster } from "react-hot-toast";

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    sentiment: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const fetchReviews = async () => {
    try {
      const res = await axios.get("/reviews/all", { params: filters });
      setReviews(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to fetch reviews");
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`/reviews/${id}/status`, { status });
      toast.success("Status updated");
      setReviews((prev) =>
        prev.map((r) => (r._id === id ? { ...r, status } : r))
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  return (
    <div className="min-h-screen py-2 px-2">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto p-6 rounded-lg">
        <h2 className="text-4xl font-extrabold text-indigo-600 mb-8">
          All Reviews
        </h2>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <input
            type="text"
            placeholder="Search title/description"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            className="border px-3 py-2 rounded-md"
          />
          <select
            value={filters.sentiment}
            onChange={(e) =>
              setFilters((f) => ({ ...f, sentiment: e.target.value }))
            }
            className="border px-3 py-2 rounded-md"
          >
            <option value="">All Sentiments</option>
            <option value="positive">Positive</option>
            <option value="neutral">Neutral</option>
            <option value="negative">Negative</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value }))
            }
            className="border px-3 py-2 rounded-md"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters((f) => ({ ...f, startDate: e.target.value }))
            }
            className="border px-3 py-2 rounded-md"
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters((f) => ({ ...f, endDate: e.target.value }))
            }
            className="border px-3 py-2 rounded-md"
          />
          <button
            onClick={fetchReviews}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md"
          >
            Apply
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
  <table className="min-w-[1000px] text-sm bg-white">
    <thead className="bg-indigo-50 text-indigo-600 sticky top-0 z-10">
      <tr>
        <th className="py-3 px-4 text-center">Sr No.</th>
        <th className="py-3 px-6 text-left">User</th>
        <th className="py-3 px-6 text-left">Tour</th>
        <th className="py-3 px-6 text-left">Rating</th>
        <th className="py-3 px-6 text-left">Title</th>
        <th className="py-3 px-6 text-left w-[300px]">Description</th>
        <th className="py-3 px-6 text-left">Sentiment</th>
        <th className="py-3 px-6 text-left">Status</th>
        <th className="py-3 px-6 text-left">Date</th>
      </tr>
    </thead>
    <tbody>
      {reviews.map((r, index) => (
        <tr
          key={r._id}
          className={`hover:bg-indigo-50 transition-colors ${
            index % 2 === 0 ? "bg-white" : "bg-gray-50"
          }`}
        >
          <td className="py-3 px-4 text-center border-b text-gray-800">
            {index + 1}
          </td>
          <td className="py-3 px-6 border-b text-gray-800">
            {r.user?.name || "-"}
          </td>
          <td className="py-3 px-6 border-b text-gray-800">
            {r.tour?.title || "-"}
          </td>
          <td className="py-3 px-6 border-b text-yellow-600 font-medium">
            {r.rating}★
          </td>
          <td className="py-3 px-6 border-b text-gray-800">{r.title}</td>
          <td className="py-3 px-6 border-b text-gray-700">
            <div
              className="line-clamp-2 cursor-help"
              title={r.description}
            >
              {r.description}
            </div>
          </td>
          <td className="py-3 px-6 border-b text-sm font-medium">
            <span
              className={`px-2 py-1 rounded-full ${
                r.sentiment === "positive"
                  ? "bg-green-100 text-green-700"
                  : r.sentiment === "negative"
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {r.sentiment || "-"}
            </span>
          </td>
          <td className="py-3 px-6 border-b">
            <select
              value={r.status}
              onChange={(e) =>
                handleStatusChange(r._id, e.target.value)
              }
              className="border border-gray-300 bg-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
            >
              <option value="active" className="text-green-600">
                Active
              </option>
              <option value="inactive" className="text-gray-600">
                Inactive
              </option>
            </select>
          </td>
          <td className="py-3 px-6 border-b text-gray-500">
            {new Date(r.createdAt).toLocaleDateString()}
          </td>
        </tr>
      ))}
      {reviews.length === 0 && (
        <tr>
          <td colSpan="9" className="text-center py-6 text-gray-500">
            No reviews found.
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>
      </div>
    </div>
  );
};

export default AdminReviews;
